import Header from "@/components/header";
import Loader from "@/components/loader";
import { ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { ORPCContext, link, type orpc } from "@/utils/orpc";
import { createORPCClient } from "@orpc/client";
import { createORPCReactQueryUtils } from "@orpc/react-query";
import type { RouterClient } from "@orpc/server";
import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
	HeadContent,
	Outlet,
	createRootRouteWithContext,
	useRouterState,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useState } from "react";
import type { appRouter } from "../../../server/src/routers";
import "../index.css";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";

import { useAuthStore } from "@/stores/auth-store";
import { useUserStore } from "@/stores/user-store";
import { useAppStore } from "@/stores/app-store";
import type { useManager } from "tinytick/ui-react";
import { useNetworkState } from "@uidotdev/usehooks";
import { ShambaODKDB } from "@/stores/indexed-db";

export interface RouterAppContext {
	orpc: typeof orpc;
	queryClient: QueryClient;
	auth: ReturnType<typeof useAuthStore>;
	user: ReturnType<typeof useUserStore>;
	manager: ReturnType<typeof useManager>;
	network: ReturnType<typeof useNetworkState>;
	app: ReturnType<typeof useAppStore>;
	db: ShambaODKDB; // IndexedDB instance
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
	beforeLoad: async ({ context }) => {
		const { manager } = context;
		manager?.scheduleTaskRun("initAuth");
	},
	component: RootComponent,
	head: () => ({
		meta: [
			{
				title: "Shamba ODK",
			},
			{
				name: "description",
				content: "Shamba ODK is an offline-first farmer management tool.",
			},
		],
		links: [
			{
				rel: "icon",
				href: "/favicon.ico",
			},
		],
		scripts: [
			{
				src: "https://rybbit.quitesimpleerp.com/api/script.js",
				"data-site-id": "4",
				"data-track-errors": "true",
				"data-session-replay": "true",
				"data-api-key": import.meta.env.VITE_RYBBIT_API_KEY,
				defer: true,
			}
		]
	}),
});

function RootComponent() {
	const [client] = useState<RouterClient<typeof appRouter>>(() =>
		createORPCClient(link),
	);
	const [orpc] = useState(() => createORPCReactQueryUtils(client));

	const isFetching = useRouterState({
		select: (s) => s.isLoading,
	});
	return (
		<>
			<HeadContent />
			<ORPCContext.Provider value={orpc}>
				<ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
					<div className="[--header-height:calc(theme(spacing.14))]">
						<SidebarProvider className="flex flex-col">
							<Header />
							<div className="flex flex-1">
								<AppSidebar />
								<SidebarInset>
									{isFetching ? <Loader /> : <Outlet />}
								</SidebarInset>
							</div>
						</SidebarProvider>
					</div>
					<Toaster richColors closeButton />
				</ThemeProvider>
			</ORPCContext.Provider>
			<TanStackRouterDevtools position="top-right" />
			<ReactQueryDevtools position="bottom" buttonPosition="bottom-right" />
		</>
	);
}
